import React, { forwardRef, useState } from 'react';
import { InputProps } from '../../types';
import { clsx } from 'clsx';

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  placeholder,
  type = 'text',
  value,
  onChange,
  disabled = false,
  required = false,
  className = '',
  icon,
  iconPosition = 'left',
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const inputClasses = clsx(
    'block w-full border rounded-xl shadow-sm placeholder-gray-400 transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300',
    'bg-white text-gray-900 font-medium',
    {
      // Normal state
      'border-gray-300 hover:border-gray-400': !error && !isFocused,
      // Focused state
      'border-slate-500 shadow-sm': !error && isFocused,
      // Error state
      'border-red-400 focus-visible:ring-red-200': error,
      // Disabled state
      'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300 hover:border-gray-300': disabled,
      // Icon padding
      'pl-12': icon && iconPosition === 'left',
      'pr-12': icon && iconPosition === 'right',
      'px-4 py-3': !icon,
    },
    className
  );

  const labelClasses = clsx(
    'block text-sm font-bold mb-2 transition-colors duration-200',
    {
      'text-gray-800': !error && !isFocused,
      'text-red-600': error,
      'text-blue-600': isFocused && !error,
    }
  );

  const iconClasses = clsx(
    'h-5 w-5 transition-colors duration-200',
    {
      'text-gray-400': !isFocused && !error,
      'text-blue-500': isFocused && !error,
      'text-red-400': error,
    }
  );

  return (
    <div className="space-y-2">
      {label && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative group">
        {icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <div className={iconClasses}>
              {icon}
            </div>
          </div>
        )}
        <input
          ref={ref}
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={inputClasses}
          {...props}
        />
        {icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            <div className={iconClasses}>
              {icon}
            </div>
          </div>
        )}
        {isFocused && !error && <div className="absolute inset-0 rounded-xl ring-2 ring-slate-200 pointer-events-none"></div>}
      </div>
      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg animate-fade-in flex items-center space-x-1 font-medium">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
